/**
 * Visual language for story topics. FEATURE_ACCENT is this feature's own map of
 * literal AccentNames (the app-wide SECTION_ACCENT stays untouched); class
 * strings always come from ACCENTS, never from string concatenation.
 */
import {
  Backpack,
  Baby,
  Briefcase,
  CloudRainWind,
  Coffee,
  Heart,
  HeartHandshake,
  Stethoscope,
  ToyBrick,
  type LucideIcon,
} from "lucide-react";
import type { AccentName } from "../../lib/accents";
import type { StoryCategory } from "./contract";

// Nine topics share six accents (some repeat); their pills differ by icon.
// Family stories, the flagship topic, keeps the warm rose.
export const FEATURE_ACCENT: Record<StoryCategory, AccentName> = {
  "Family stories": "rose",
  "New parents": "sky",
  Toddlers: "amber",
  School: "violet",
  Health: "emerald",
  "Typhoon & rainy season": "sky",
  "Working parents": "amber",
  "Mental health": "violet",
  Dads: "indigo",
};

export const CATEGORY_ICON: Record<StoryCategory, LucideIcon> = {
  "Family stories": Heart,
  "New parents": Baby,
  Toddlers: ToyBrick,
  School: Backpack,
  Health: Stethoscope,
  "Typhoon & rainy season": CloudRainWind,
  "Working parents": Briefcase,
  "Mental health": HeartHandshake,
  Dads: Coffee,
};

/** Soft multi-pastel washes (literal classes, mirroring PlayfulHero's bands). */
export const COVER_WASH: Record<AccentName, string> = {
  sky: "bg-gradient-to-br from-sky-100 via-violet-50 to-rose-50",
  rose: "bg-gradient-to-br from-rose-100 via-pink-50 to-amber-50",
  amber: "bg-gradient-to-br from-amber-100 via-orange-50 to-rose-50",
  emerald: "bg-gradient-to-br from-emerald-100 via-teal-50 to-sky-50",
  violet: "bg-gradient-to-br from-violet-100 via-purple-50 to-sky-50",
  indigo: "bg-gradient-to-br from-indigo-100 via-sky-50 to-violet-50",
};

/** Accent icon color for inactive pills (decorative icons, -600 per the chip contract). */
export const ICON_TEXT: Record<AccentName, string> = {
  sky: "text-sky-600",
  rose: "text-rose-600",
  amber: "text-amber-600",
  emerald: "text-emerald-600",
  violet: "text-violet-600",
  indigo: "text-indigo-600",
};

/** Bullet dots in a story body. */
export const BULLET_DOT: Record<AccentName, string> = {
  sky: "bg-sky-400",
  rose: "bg-rose-400",
  amber: "bg-amber-400",
  emerald: "bg-emerald-400",
  violet: "bg-violet-400",
  indigo: "bg-indigo-400",
};
